// app/components/privacy-policy/PrivacyPolicy.tsx
"use client";

import * as React from "react";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import { Box, List, ListItem } from "@mui/material";
import { useTranslations, useLocale } from "next-intl";
import Link from "next/link";
import { usePathname } from "next/navigation";

type InlineEdit = {
  id: string;
  type: "text" | "image";
  selector: string;
  value: string;
  cmsKey?: string;
};

export default function PrivacyPolicy() {
  const t = useTranslations("PrivacyPolicy.Content");
  const locale = useLocale();
  const pathname = usePathname() || "/";

  // ---- map pathname → path ที่ใช้ใน CMS (ตัด prefix /en /zh ถ้ามี) ----
  const pagePath = React.useMemo(() => {
    const parts = pathname.split("/");
    if (parts[1] === "en" || parts[1] === "zh") {
      const tail = parts.slice(2).join("/");
      return tail ? `/${tail}` : "/";
    }
    return pathname || "/";
  }, [pathname]);

  // ---- default values จาก i18n (SEO friendly / SSR) ----
  const defaultTitle = t("title");
  const defaultIntro = t("intro");
  const defaultPurposesTitle = t("purposesTitle");
  const defaultPurposesList = t.raw("purposesList") as string[];

  const defaultCollectionCasesTitle = t("collectionCasesTitle");
  const defaultCase1Title = t("collectionCasesList.case1.title");
  const defaultCase1Items = t.raw("collectionCasesList.case1.items") as string[];
  const defaultCase2Title = t("collectionCasesList.case2.title");
  const defaultCase2Items = t.raw("collectionCasesList.case2.items") as string[];
  const defaultCase3 = t("collectionCasesList.case3");

  const defaultCookiesTitle = t("cookiesTitle");
  const defaultCookiesContent = t("cookiesContent"); // base string (ใช้คู่กับ t.rich ถ้าไม่มี inline override)

  const defaultRetentionTitle = t("retentionPeriodTitle");
  const defaultRetentionContent = t("retentionPeriodContent");
  const defaultDeletionTitle = t("deletionTitle");
  const defaultDeletionContent = t("deletionContent");
  const defaultUserRightsTitle = t("userRightsTitle");
  const defaultUserRightsContent = t("userRightsContent");
  const defaultRelatedPoliciesTitle = t("relatedPoliciesTitle");

  // ---- state จริงที่เอาไว้ render (ให้ inline override ได้) ----
  const [title, setTitle] = React.useState(defaultTitle);
  const [intro, setIntro] = React.useState(defaultIntro);

  const [purposesTitle, setPurposesTitle] =
    React.useState(defaultPurposesTitle);
  const [purposesList, setPurposesList] =
    React.useState<string[]>(defaultPurposesList);

  const [collectionCasesTitle, setCollectionCasesTitle] =
    React.useState(defaultCollectionCasesTitle);
  const [case1Title, setCase1Title] = React.useState(defaultCase1Title);
  const [case1Items, setCase1Items] =
    React.useState<string[]>(defaultCase1Items);
  const [case2Title, setCase2Title] = React.useState(defaultCase2Title);
  const [case2Items, setCase2Items] =
    React.useState<string[]>(defaultCase2Items);
  const [case3, setCase3] = React.useState(defaultCase3);

  const [cookiesTitle, setCookiesTitle] =
    React.useState(defaultCookiesTitle);
  const [cookiesContentInline, setCookiesContentInline] =
    React.useState<string | null>(null); // ถ้ามี override จะใช้ตัวนี้

  const [retentionTitle, setRetentionTitle] =
    React.useState(defaultRetentionTitle);
  const [retentionContent, setRetentionContent] =
    React.useState(defaultRetentionContent);

  const [deletionTitle, setDeletionTitle] =
    React.useState(defaultDeletionTitle);
  const [deletionContent, setDeletionContent] =
    React.useState(defaultDeletionContent);

  const [userRightsTitle, setUserRightsTitle] =
    React.useState(defaultUserRightsTitle);
  const [userRightsContent, setUserRightsContent] =
    React.useState(defaultUserRightsContent);

  const [relatedPoliciesTitle, setRelatedPoliciesTitle] =
    React.useState(defaultRelatedPoliciesTitle);

  // ---- reset ตาม locale (เริ่มจากข้อความในไฟล์แปลก่อนทุกครั้ง) ----
  React.useEffect(() => {
    setTitle(defaultTitle);
    setIntro(defaultIntro);

    setPurposesTitle(defaultPurposesTitle);
    setPurposesList(defaultPurposesList);

    setCollectionCasesTitle(defaultCollectionCasesTitle);
    setCase1Title(defaultCase1Title);
    setCase1Items(defaultCase1Items);
    setCase2Title(defaultCase2Title);
    setCase2Items(defaultCase2Items);
    setCase3(defaultCase3);

    setCookiesTitle(defaultCookiesTitle);
    setCookiesContentInline(null);

    setRetentionTitle(defaultRetentionTitle);
    setRetentionContent(defaultRetentionContent);
    setDeletionTitle(defaultDeletionTitle);
    setDeletionContent(defaultDeletionContent);
    setUserRightsTitle(defaultUserRightsTitle);
    setUserRightsContent(defaultUserRightsContent);
    setRelatedPoliciesTitle(defaultRelatedPoliciesTitle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);

 //
  React.useEffect(() => {
    const fetchInline = async () => {
      try {
        const res = await fetch(
          `/api/admin/page-inline?websiteId=${process.env.NEXT_PUBLIC_WEBSITE_ID || process.env.WEBSITE_ID}&path=${encodeURIComponent(
            pagePath
          )}&locale=${locale}`,
          {
            credentials: "include",
            cache: "no-store",
          }
        );

        if (!res.ok) return;

        const data = await res.json();
        if (!data?.edits) return;

        const list: InlineEdit[] = data.edits;
        const map: Record<string, string> = {};

        list.forEach((e) => {
          if (e.cmsKey) {
            map[e.cmsKey] = e.value;
            return;
          }
          const match = e.selector?.match(/data-cms-key="([^"]+)"/);
          if (match) {
            map[match[1]] = e.value;
          }
        });

        // ---- override ฟิลด์เดี่ยว ๆ ----
        if (map["privacy.title"]) setTitle(map["privacy.title"]);
        if (map["privacy.intro"]) setIntro(map["privacy.intro"]);

        if (map["privacy.purposesTitle"])
          setPurposesTitle(map["privacy.purposesTitle"]);

        if (map["privacy.collectionCasesTitle"])
          setCollectionCasesTitle(map["privacy.collectionCasesTitle"]);
        if (map["privacy.case1.title"])
          setCase1Title(map["privacy.case1.title"]);
        if (map["privacy.case2.title"])
          setCase2Title(map["privacy.case2.title"]);
        if (map["privacy.case3"]) setCase3(map["privacy.case3"]);

        if (map["privacy.cookiesTitle"])
          setCookiesTitle(map["privacy.cookiesTitle"]);
        if (map["privacy.cookiesContent"])
          setCookiesContentInline(map["privacy.cookiesContent"]);

        if (map["privacy.retentionPeriodTitle"])
          setRetentionTitle(map["privacy.retentionPeriodTitle"]);
        if (map["privacy.retentionPeriodContent"])
          setRetentionContent(map["privacy.retentionPeriodContent"]);

        if (map["privacy.deletionTitle"])
          setDeletionTitle(map["privacy.deletionTitle"]);
        if (map["privacy.deletionContent"])
          setDeletionContent(map["privacy.deletionContent"]);

        if (map["privacy.userRightsTitle"])
          setUserRightsTitle(map["privacy.userRightsTitle"]);
        if (map["privacy.userRightsContent"])
          setUserRightsContent(map["privacy.userRightsContent"]);

        if (map["privacy.relatedPoliciesTitle"])
          setRelatedPoliciesTitle(map["privacy.relatedPoliciesTitle"]);

        // ---- override list แบบเป็น item ----
        // purposesList
        if (defaultPurposesList.length > 0) {
          const next = [...defaultPurposesList];
          for (let i = 0; i < next.length; i++) {
            const key = `privacy.purposesList.${i}`;
            if (map[key]) next[i] = map[key];
          }
          setPurposesList(next);
        }

        // case1 items
        if (defaultCase1Items.length > 0) {
          const next = [...defaultCase1Items];
          for (let i = 0; i < next.length; i++) {
            const key = `privacy.case1.items.${i}`;
            if (map[key]) next[i] = map[key];
          }
          setCase1Items(next);
        }

        // case2 items
        if (defaultCase2Items.length > 0) {
          const next = [...defaultCase2Items];
          for (let i = 0; i < next.length; i++) {
            const key = `privacy.case2.items.${i}`;
            if (map[key]) next[i] = map[key];
          }
          setCase2Items(next);
        }
      } catch (err) {
        console.warn("[PrivacyPolicy] inline fetch error:", err);
      }
    };

    fetchInline();
  }, [
    locale,
    pagePath,
    defaultPurposesList,
    defaultCase1Items,
    defaultCase2Items,
  ]);

  // ---- render ----
  return (
    <Box className="policy-wrap" sx={{
      py: 10
    }}>
      <Container className="container">
        <Typography
          variant="h4"
          data-aos="fade-up"
          data-cms-key="privacy.title"
          sx={{
            mb: 3
          }}
        >
          {title}
        </Typography>

        <Typography
          variant="body1"
          data-aos="fade-up"
          data-cms-key="privacy.intro"
          sx={{
            mb: 3
          }}
        >
          {intro}
        </Typography>

        <Typography
          variant="h4"
          data-aos="fade-up"
          data-cms-key="privacy.purposesTitle"
          sx={{
            mb: 3
          }}
        >
          {purposesTitle}
        </Typography>

        <List
          sx={{ listStyleType: "decimal", pl: 4, mb: 3 }}
          data-aos="fade-up"
        >
          {purposesList.map((item, index) => (
            <ListItem
              key={index}
              sx={{ display: "list-item", pl: 0 }}
            >
              <Typography
                variant="body1"
                data-cms-key={`privacy.purposesList.${index}`}
              >
                {item}
              </Typography>
            </ListItem>
          ))}
        </List>

        <Typography
          variant="h4"
          data-aos="fade-up"
          data-cms-key="privacy.collectionCasesTitle"
          sx={{
            mb: 3
          }}
        >
          {collectionCasesTitle}
        </Typography>

        <List
          sx={{ listStyleType: "decimal", pl: 4, mb: 3 }}
          data-aos="fade-up"
        >
          {/* Case 1 */}
          <ListItem
            sx={{
              display: "list-item",
              pl: 0,
              flexDirection: "column",
              alignItems: "flex-start",
            }}
          >
            <Typography
              variant="body1"
              data-cms-key="privacy.case1.title"
            >
              {case1Title}
            </Typography>
            <List sx={{ listStyleType: "disc", pl: 4 }}>
              {case1Items.map((item, index) => (
                <ListItem
                  key={index}
                  sx={{ display: "list-item", pl: 0 }}
                >
                  <Typography
                    variant="body1"
                    data-cms-key={`privacy.case1.items.${index}`}
                  >
                    {item}
                  </Typography>
                </ListItem>
              ))}
            </List>
          </ListItem>

          {/* Case 2 */}
          <ListItem
            sx={{
              display: "list-item",
              pl: 0,
              flexDirection: "column",
              alignItems: "flex-start",
            }}
          >
            <Typography
              variant="body1"
              data-cms-key="privacy.case2.title"
            >
              {case2Title}
            </Typography>
            <List sx={{ listStyleType: "disc", pl: 4 }}>
              {case2Items.map((item, index) => (
                <ListItem
                  key={index}
                  sx={{ display: "list-item", pl: 0 }}
                >
                  <Typography
                    variant="body1"
                    data-cms-key={`privacy.case2.items.${index}`}
                  >
                    {item}
                  </Typography>
                </ListItem>
              ))}
            </List>
          </ListItem>

          {/* Case 3 */}
          <ListItem sx={{ display: "list-item", pl: 0 }}>
            <Typography
              variant="body1"
              data-cms-key="privacy.case3"
            >
              {case3}
            </Typography>
          </ListItem>
        </List>

        <Typography
          variant="h4"
          data-aos="fade-up"
          data-cms-key="privacy.cookiesTitle"
          sx={{
            mb: 3
          }}
        >
          {cookiesTitle}
        </Typography>

        {/* ถ้ามี inline override → ใช้ข้อความตรง ๆ; ถ้าไม่มีก็ fallback ไป t.rich เดิม */}
        {cookiesContentInline ? (
          <Typography
            variant="body1"
            data-aos="fade-up"
            data-cms-key="privacy.cookiesContent"
            sx={{
              mb: 3
            }}
          >
            {cookiesContentInline}
          </Typography>
        ) : (
          <Typography
            variant="body1"
            data-aos="fade-up"
            data-cms-key="privacy.cookiesContent"
            sx={{
              mb: 3
            }}
          >
            {t.rich("cookiesContent", {
              link: (chunks) => <Link href="/cookies-policy">{chunks}</Link>,
            })}
          </Typography>
        )}

        <Typography
          variant="h4"
          data-aos="fade-up"
          data-cms-key="privacy.retentionPeriodTitle"
          sx={{
            mb: 3
          }}
        >
          {retentionTitle}
        </Typography>
        <Typography
          variant="body1"
          data-aos="fade-up"
          data-cms-key="privacy.retentionPeriodContent"
          sx={{
            mb: 3
          }}
        >
          {retentionContent}
        </Typography>

        <Typography
          variant="h4"
          data-aos="fade-up"
          data-cms-key="privacy.deletionTitle"
          sx={{
            mb: 3
          }}
        >
          {deletionTitle}
        </Typography>
        <Typography
          variant="body1"
          data-aos="fade-up"
          data-cms-key="privacy.deletionContent"
          sx={{
            mb: 3
          }}
        >
          {deletionContent}
        </Typography>

        <Typography
          variant="h4"
          data-aos="fade-up"
          data-cms-key="privacy.userRightsTitle"
          sx={{
            mb: 3
          }}
        >
          {userRightsTitle}
        </Typography>
        <Typography
          variant="body1"
          data-aos="fade-up"
          data-cms-key="privacy.userRightsContent"
          sx={{
            mb: 3
          }}
        >
          {userRightsContent}
        </Typography>

        <Typography
          variant="h4"
          data-aos="fade-up"
          data-cms-key="privacy.relatedPoliciesTitle"
          sx={{
            mb: 3
          }}
        >
          {relatedPoliciesTitle}
        </Typography>
      </Container>
    </Box>
  );
}